import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import {
  Users,
  GraduationCap,
  FileText,
  Settings,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Quote,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { ConsultantAvatar } from "@/components/ConsultantAvatar";
import techCorpLogo from "@/assets/techcorp-logo.png";
import thalesLogo from "@/assets/thales-logo.png";
import backbaseLogo from "@/assets/backbase-logo.png";
import volksbankLogo from "@/assets/volksbank-logo.png";
import greenfluxLogo from "@/assets/greenflux-logo.png";
import yukiLogo from "@/assets/yuki-logo.png";
import rolandFlemmImg from "@/assets/roland-flemm.png";
import viktorGlinkaImg from "@/assets/viktor-glinka.png";
import aimeFlemmImg from "@/assets/aime-flemm.png";
import dagTopologiesBadge from "@/assets/dag-topologies-badge.png";
import logoOrren from "@/assets/logo-orren.png";

const testimonials = [
  {
    quote:
      "Orren's workshop transformed our team dynamics completely. The practical approach and expert facilitation delivered results beyond our expectations.",
    author: "Sarah Johnson",
    position: "CEO",
    company: "TechCorp Solutions",
  },
  {
    quote:
      "The training program was exceptional. Our staff gained invaluable skills that immediately improved our operational efficiency by 40%.",
    author: "Michael Chen",
    position: "Operations Director",
    company: "Global Industries",
  },
  {
    quote:
      "Their strategic advice report provided the roadmap we needed. The implementation support ensured seamless execution of our transformation.",
    author: "Emily Rodriguez",
    position: "Innovation Lead",
    company: "InnovateX",
  },
  {
    quote:
      "Professional, insightful, and results-driven. Orren helped us navigate complex challenges with confidence and clarity.",
    author: "David Thompson",
    position: "Managing Partner",
    company: "Summit Financial",
  },
  {
    quote:
      "The comprehensive approach from analysis to implementation made all the difference. Truly exceptional consulting services.",
    author: "Lisa Wang",
    position: "Chief Strategy Officer",
    company: "NextGen Healthcare",
  },
  {
    quote:
      "Outstanding expertise and dedication. Orren delivered solutions that exceeded our most optimistic projections.",
    author: "Robert Kim",
    position: "Executive VP",
    company: "Meridian Consulting",
  },
];

const TestimonialsContent = () => {
  const { elementRef, isVisible } = useScrollReveal();

  return (
    <div
      ref={elementRef}
      className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}
    >
      <div className="text-center mb-16">
        <Badge variant="secondary" className="mb-4">
          Client Success Stories
        </Badge>
        <h2 className="text-4xl font-bold mb-6 text-foreground">Trusted by Industry Leaders</h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Discover how we have helped organizations achieve remarkable transformations and sustainable growth.
        </p>
      </div>

      {/* Testimonials Carousel */}
      <Carousel className="w-full max-w-6xl mx-auto mb-16">
        <CarouselContent>
          {testimonials.map((testimonial) => (
            <CarouselItem key={`${testimonial.author}-${testimonial.company}`} className="md:basis-1/2 lg:basis-1/3">
              <div className="p-1">
                <Card className="group hover:shadow-medium transition-all duration-300 border-border/50 bg-card/90 backdrop-blur-sm h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Quote className="h-8 w-8 text-accent mb-4 opacity-50" />
                    <blockquote className="text-muted-foreground mb-6 leading-relaxed italic flex-grow">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="border-t pt-4">
                      <div className="font-semibold text-foreground">{testimonial.author}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.position}</div>
                      <div className="text-sm font-medium text-primary mt-1">{testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>

      {/* Company Logos Carousel */}
      <div className="text-center">
        <h3 className="text-2xl font-semibold mb-8 text-foreground">Trusted by Leading Companies</h3>
        <Carousel className="w-full max-w-4xl mx-auto">
          <CarouselContent>
            {[
              { src: thalesLogo, alt: "Thales logo", url: "https://www.thalesgroup.com/en" },
              { src: backbaseLogo, alt: "Backbase logo", url: "https://www.backbase.com/" },
              { src: volksbankLogo, alt: "Volksbank logo", url: "https://corporate.asnbank.nl/en/" },
              { src: greenfluxLogo, alt: "Greenflux logo", url: "https://www.greenflux.com/" },
              { src: yukiLogo, alt: "Yuki logo", url: "https://www.yukisoftware.com/nl-nl/" },
            ].map((logo, index) => (
              <CarouselItem key={index} className="basis-1/2 md:basis-1/3 lg:basis-1/4">
                <div className="p-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-soft hover:shadow-medium transition-shadow">
                    {logo.url ? (
                      <a href={logo.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="w-full h-24 object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                        />
                      </a>
                    ) : (
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="w-full h-24 object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                      />
                    )}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
    </div>
  );
};

const Index = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Users,
      title: "Workshops",
      description:
        "Interactive, hands-on workshops designed to upskill your team and drive organizational change through collaborative learning experiences.",
      features: ["Custom curriculum", "Expert facilitation", "Practical outcomes", "Follow-up support"],
      path: "/workshops",
    },
    {
      icon: GraduationCap,
      title: "Training",
      description:
        "Comprehensive training programs tailored to your industry needs, delivered by seasoned professionals with real-world expertise.",
      features: ["Industry expertise", "Flexible delivery", "Certification options", "Ongoing mentorship"],
      path: "/training",
    },
    {
      icon: FileText,
      title: "Advice Reports",
      description:
        "In-depth analytical reports providing strategic insights and actionable recommendations to optimize your business operations.",
      features: ["Data-driven insights", "Strategic recommendations", "Implementation roadmaps", "Executive summaries"],
      path: "/advice-reports",
    },
    {
      icon: Settings,
      title: "Transformations",
      description:
        "Redefining your organization’s ways of working and operating models to drive sustainable performance and value delivery.",
      features: ["Operating model design", "Change management", "Cultural alignment", "Performance monitoring"],
      path: "/transformations",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header>
        <nav
          className="fixed top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={logoOrren} alt="Orren Business Consulting Logo" className="w-8 h-8 object-contain" />
                <div className="text-2xl font-vailery text-primary">Orren Consultancy</div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="#services" className="text-muted-foreground hover:text-primary transition-colors">
                  Services
                </a>
                <a href="#about" className="text-muted-foreground hover:text-primary transition-colors">
                  About
                </a>
                <Link to="/book-consultation" className="text-muted-foreground hover:text-primary transition-colors">
                  Contact
                </Link>
                <Button variant="default" className="bg-gradient-primary" asChild>
                  <Link to="/book-consultation">
                    Get Started <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-hero relative overflow-hidden" aria-labelledby="hero-heading">
          <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <Badge variant="secondary" className="mb-6 bg-white/10 text-white border-white/20">
                Professional Consulting Services
              </Badge>
              <h1 id="hero-heading" className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in text-center">
                Every transformation is unique
                <span className="block text-accent"> Lets discover yours</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90 animate-slide-up">
                Orren delivers comprehensive consulting solutions through workshops, training, strategic advice, and
                seamless transformations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-strong" asChild>
                  <Link to="/strategy-check">Free Strategy Check Tool</Link>
                </Button>
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-strong" asChild>
                  <Link to="/book-consultation">Book Free Consultation</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-secondary/30" aria-labelledby="services-heading">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <Badge variant="secondary" className="mb-4">
                Our Expertise
              </Badge>
              <h2 id="services-heading" className="text-4xl font-bold mb-6 text-foreground">
                Professional Consulting Services
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                We provide comprehensive consulting services designed to drive meaningful change and sustainable growth
                in your organization.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => {
                const ServiceCard = () => {
                  const { elementRef, isVisible } = useScrollReveal();

                  return (
                    <div
                      ref={elementRef}
                      className={`transition-all duration-700 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                      }`}
                      style={{ transitionDelay: `${index * 150}ms` }}
                    >
                      <Card
                        className="group hover:shadow-medium transition-all duration-300 border-border/50 bg-card/80 backdrop-blur-sm cursor-pointer h-full"
                        onClick={() => navigate(service.path)}
                      >
                        <CardHeader className="text-center pb-4">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-primary rounded-lg flex items-center justify-center group-hover:animate-float">
                            <service.icon className="h-8 w-8 text-white" />
                          </div>
                          <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-center mb-6 leading-relaxed">
                            {service.description}
                          </CardDescription>
                          <ul className="space-y-2">
                            {service.features.map((feature) => (
                              <li key={feature} className="flex items-center text-sm text-muted-foreground">
                                <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  );
                };

                return <ServiceCard key={service.title} />;
              })}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20" aria-labelledby="about-heading">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-4">
                About Orren
              </Badge>
              <h2 id="about-heading" className="text-4xl font-bold mb-8 text-foreground">
                Expert Business Consultants
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                At Orren, we believe that sustainable business transformation requires the right combination of
                strategic thinking, practical expertise, and seamless execution. Our team of seasoned consultants brings
                decades of industry experience across diverse sectors, enabling us to deliver solutions that not only
                meet your immediate needs but also position your organization for long-term success.
              </p>
              {/* Stats temporarily hidden */}

              {/* Our Consultants Subsection */}
              <div className="mt-20">
                <h3 className="text-3xl font-bold mb-4 text-foreground text-center">Professional Consulting Team</h3>
                <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                  Meet the experts who will guide your transformation journey with deep industry knowledge and proven
                  methodologies.
                </p>
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    {
                      name: "Roland Flemm",
                      url: "https://www.linkedin.com/in/rolandflemm/",
                      image: rolandFlemmImg,
                      title: "Partner",
                    },
                    {
                      name: "Viktor Glinka",
                      url: "https://www.linkedin.com/in/viktorglinka/",
                      image: viktorGlinkaImg,
                      title: "Senior Consultant",
                    },
                    {
                      name: "Aimé Flemm",
                      url: "https://www.linkedin.com/in/aim%C3%A9-flemm/",
                      image: aimeFlemmImg,
                      title: "Consultant",
                    },
                  ].map((consultant, index) => {
                    const ConsultantCard = () => {
                      const { elementRef, isVisible } = useScrollReveal();

                      return (
                        <div
                          ref={elementRef}
                          className={`transition-all duration-700 ${
                            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                          }`}
                          style={{ transitionDelay: `${index * 150}ms` }}
                        >
                          <a
                            href={consultant.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative overflow-hidden rounded-lg border-2 border-border/50 bg-card/80 backdrop-blur-sm hover:border-primary/50 hover:shadow-medium transition-all duration-300 p-8 text-center cursor-pointer block"
                          >
                            <ConsultantAvatar
                              imageSrc={consultant.image}
                              name={consultant.name}
                              className="w-32 h-32 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                            />
                            <div className="space-y-2">
                              <h4 className="text-xl font-semibold text-foreground">{consultant.name}</h4>
                              <p className="text-sm text-muted-foreground">{consultant.title}</p>
                            </div>
                            <div className="mt-4 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                              View LinkedIn Profile
                            </div>
                          </a>
                        </div>
                      );
                    };

                    return <ConsultantCard key={consultant.name} />;
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gradient-secondary" aria-labelledby="testimonials-heading">
          <div className="container mx-auto px-6">
            <TestimonialsContent />
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gradient-secondary" aria-labelledby="contact-heading">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">
                  Get In Touch
                </Badge>
                <h2 id="contact-heading" className="text-4xl font-bold mb-6 text-foreground">
                  Contact Our Consulting Team
                </h2>
                <p className="text-xl text-muted-foreground">
                  Let's discuss how Orren can help you achieve your organizational goals.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-6 text-foreground">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted-foreground">info@orren.com</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted-foreground">+31 6 16 76 78 05</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted-foreground">Utrecht, Netherlands</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold mb-6 text-foreground">Schedule a Consultation</h3>
                  <p className="text-muted-foreground mb-6">
                    Ready to get started? Reach out to us for a complimentary consultation to discuss your specific
                    needs and how we can help.
                  </p>
                  <Button asChild id="book-consultation" size="lg" className="w-full bg-gradient-primary shadow-medium">
                    <Link to="/book-consultation">
                      Book Free Consultation
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-primary text-primary-foreground" role="contentinfo">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="text-3xl font-bold mb-4">Orren Consultancy</div>
              <p className="text-primary-foreground/80 mb-6">Professional Services</p>
              <div className="text-sm text-primary-foreground/60">© 2025 Orren. All rights reserved.</div>
            </div>
            <div className="flex-shrink-0">
              <img
                src={dagTopologiesBadge}
                alt="DAG Topologies Partner Enterprise Badge - Business Consulting Partnership"
                className="w-32 h-32 object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
